import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Spinner,
  Badge, Table, Form, OverlayTrigger, Tooltip, Stack, ProgressBar, Alert, Modal
} from 'react-bootstrap';
import { Joyride, STATUS } from 'react-joyride';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { addNotification, clearNotification, setTenantModules } from '../redux/slices/userSlice';
import { getCurrentUser } from '../utils/auth';
import AppHeader from '../components/Common/AppHeader';
import AppFooter from '../components/Common/AppFooter';
import NotificationBanner from '../components/Common/NotificationBanner';
import CurriculumMappingEditor from '../components/Projects/CurriculumMappingEditor';
import CurriculumInitialStep from '../components/Projects/CurriculumInitialStep';
import SelfContentPicker from '../components/Projects/SelfContentPicker';

import api, { getProjects, getContentStandards, updateProjectContentStandard, runOutcomeMapping, getTenantModules } from '../api/api';
import ContentStandardInspectModal from '../components/Common/ContentStandardInspectModal';

const LockedFeature = ({ title, reason }) => (
  <div style={{
    padding: '28px 32px', borderRadius: 12, border: '1.5px dashed #cbd5e1',
    background: '#f8fafc', textAlign: 'center', color: '#94a3b8',
  }}>
    <div style={{ fontSize: 28, marginBottom: 10 }}>🔒</div>
    <div style={{ fontWeight: 700, fontSize: 14, color: '#475569', marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>{reason}</div>
  </div>
);

const DashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenantModules = useSelector((s) => s.user?.tenantModules || ["examination"]);
  const canContentGen = tenantModules.includes('content_generation');
  const canAssessBank = tenantModules.includes('assessment_bank');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orchestratingUnits, setOrchestratingUnits] = useState({});
  const [contentBlocksByProject, setContentBlocksByProject] = useState({});
  const [finalizingUnit, setFinalizingUnit] = useState({});
  const [contentStandards, setContentStandards] = useState([]);
  const [generatingBank, setGeneratingBank] = useState({});
  const [projectBlueprints, setProjectBlueprints] = useState({});
  const [initiatingCKB, setInitiatingCKB] = useState({});
  const [ckbConfirmModal, setCkbConfirmModal] = useState({ show: false, projectId: null });

  // New State for Blueprint Selection
  const [availableBlueprints, setAvailableBlueprints] = useState([]);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Content standard inspect (read-only — SME cannot clone)
  const [inspectStdId, setInspectStdId]   = useState(null);
  const [inspectStdOpen, setInspectStdOpen] = useState(false);

  // Outcome Mapping Engine
  const [omRunning, setOmRunning] = useState({});  // { [projectId]: true }

  const handleRunOutcomeMapping = async (projectId) => {
    setOmRunning(prev => ({ ...prev, [projectId]: true }));
    try {
      const res = await runOutcomeMapping(slug, projectId);
      const d = res.data;
      if (d.status === 'skipped') {
        dispatch(addNotification({ message: `Outcome mapping skipped: ${d.reason}`, variant: 'warning' }));
      } else {
        dispatch(addNotification({
          message: `Outcome mapping complete — ${d.mappings_created} concept-LO links created.`,
          variant: 'success',
        }));
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status >= 400 && status < 500) {
        dispatch(addNotification({
          message: err.response?.data?.detail || 'Outcome mapping failed.',
          variant: 'danger',
        }));
      } else {
        dispatch(addNotification({
          message: 'Outcome mapping is running in the background.',
          variant: 'info',
        }));
      }
    } finally {
      setOmRunning(prev => ({ ...prev, [projectId]: false }));
    }
  };


  const [runTour, setRunTour] = useState(false);
  const [steps, setSteps] = useState([]);

  const user = getCurrentUser();
  const slug = user?.tenant_slug || null;
  const canReviewContent = ['sme', 'tenant_admin', 'system_admin'].includes(user?.role);
  const [wallet, setWallet] = useState(null);

  const fetchProjects = useCallback(async () => {
    if (!slug) return;
    try {
      const [projRes, walletRes, stdRes] = await Promise.all([
        getProjects(),
        api.get(`/tenant/${slug}/wallet/balance`),
        getContentStandards(slug)
      ]);
      setProjects(projRes.data);
      setWallet(walletRes.data);
      setContentStandards(stdRes.data);
      try {
        const modRes = await getTenantModules(slug);
        dispatch(setTenantModules(modRes.modules?.length ? modRes.modules : ["examination"]));
      } catch { dispatch(setTenantModules(["examination"])); }

      projRes.data.forEach(async (proj) => {
        try {
          const bpRes = await api.get(`/tenant/${slug}/projects/${proj.id}/blueprints`);
          setProjectBlueprints(prev => ({ ...prev, [proj.id]: bpRes.data.length }));
        } catch (e) { console.error("BP Check failed", proj.id); }
      });

      if (canReviewContent) {
        projRes.data.forEach(async (proj) => {
          try {
            const cbRes = await api.get(`/tenant/${slug}/admin/projects/${proj.id}/content-blocks`);
            const byUnit = {};
            (cbRes.data || []).forEach(b => { byUnit[b.unit_id] = b; });
            setContentBlocksByProject(prev => ({ ...prev, [proj.id]: byUnit }));
          } catch (e) { /* not assigned as SME on this project, or no blocks yet */ }
        });
      }

      if (projRes.data.length > 0 && steps.length === 0) {
        prepareTour();
      }
    } catch (err) {
      console.error("Data load failed", err);
    } finally {
      setLoading(false);
    }
  }, [slug, steps.length, canReviewContent]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    const hasActiveTask = projects.some(p =>
      p.curriculum_workflow_status === 'drafting' ||
      p.status === 'processing' ||
      p.current_stage === 'ckb_processing' ||
      (p.orchestration_progress && Object.values(p.orchestration_progress).some(v => v === 'generating' || v === 'in_progress'))
    );

    if (hasActiveTask) {
      const interval = setInterval(() => fetchProjects(), 10000);
      return () => clearInterval(interval);
    }
  }, [projects, fetchProjects]);

  const prepareTour = () => {
    setSteps([{
        target: '.pipeline-header',
        content: 'Welcome to your Production Pipeline. Manage your AI content lifecycle here.',
        placement: 'bottom',
        disableBeacon: true,
    }]);
    setRunTour(true);
  };

  const handleJoyrideCallback = (data) => {
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(data.status)) {
      setRunTour(false);
    }
  };

  const handleInitiateCKB = async () => {
    const projectId = ckbConfirmModal.projectId;
    if (!projectId) return;

    setCkbConfirmModal({ show: false, projectId: null });

    // Optimistic update: show "building" immediately
    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, ckb_status: 'processing', current_stage: 'ckb_processing' } : p
    ));

    try {
      await api.post(`/tenant/${slug}/projects/${projectId}/initiate-ckb`);
      dispatch(addNotification({ message: 'CKB Extraction is running in the background.', variant: 'info' }));
    } catch (err) {
      const status = err?.response?.status;
      if (status >= 400 && status < 500) {
        // Definitive client error (bad request, 402, 404) — revert and tell the user
        setProjects(prev => prev.map(p =>
          p.id === projectId ? { ...p, ckb_status: 'error', current_stage: 'blueprint' } : p
        ));
        dispatch(addNotification({
          message: err.response?.data?.detail || 'CKB failed to start. Please check your setup and try again.',
          variant: 'danger',
        }));
      } else {
        // Network timeout or server-side hiccup — the background task very likely started.
        // Keep the optimistic state; polling will update it once the task completes.
        dispatch(addNotification({
          message: 'CKB is processing in the background. The page will update automatically.',
          variant: 'info',
        }));
      }
    }
  };

  // Modified to open modal instead of immediate trigger
  const handleTriggerFullAssessment = async (projectId) => {
    setAvailableBlueprints([]);
    try {
      const res = await api.get(`/tenant/${slug}/projects/${projectId}/blueprints`);
      const data = Array.isArray(res.data) ? res.data : [];
      setAvailableBlueprints(data);
      setSelectedProjectId(projectId);
      setShowBlueprintModal(true);
    } catch (err) {
      console.error("Failed to fetch blueprints", err);
      dispatch(addNotification({ message: 'Could not load blueprints. Please refresh and try again.', variant: 'danger' }));
    }
  };

  const executeAssessmentGeneration = async (blueprintId) => {
    setGeneratingBank(prev => ({ ...prev, [selectedProjectId]: true }));
    setShowBlueprintModal(false);
    // Optimistic: show project as "processing"
    setProjects(prev => prev.map(p =>
      p.id === selectedProjectId ? { ...p, status: 'processing' } : p
    ));
    try {
      await api.post(`/tenant/${slug}/projects/${selectedProjectId}/generate-assessment-bank`, {
        blueprint_id: blueprintId, unit_id: 0
      });
      dispatch(addNotification({ message: 'Question Bank generation started in the background.', variant: 'success' }));
      fetchProjects();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        setProjects(prev => prev.map(p =>
          p.id === selectedProjectId ? { ...p, status: 'approved' } : p
        ));
        dispatch(addNotification({
          message: err.response?.data?.detail || 'Insufficient credits to generate the Question Bank.',
          variant: 'danger',
        }));
      } else if (status >= 400 && status < 500) {
        setProjects(prev => prev.map(p =>
          p.id === selectedProjectId ? { ...p, status: 'approved' } : p
        ));
        dispatch(addNotification({
          message: err.response?.data?.detail || 'Question Bank generation failed.',
          variant: 'danger',
        }));
      } else {
        // Network/server timeout — generation task very likely started
        dispatch(addNotification({
          message: 'Question Bank generation is running in the background. Refresh in a few minutes.',
          variant: 'info',
        }));
      }
    } finally {
      setGeneratingBank(prev => ({ ...prev, [selectedProjectId]: false }));
    }
  };

  const handleStandardChange = async (projectId, standardId) => {
    try {
      await updateProjectContentStandard(slug, projectId, standardId);
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, content_standard_id: standardId } : p));
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  const handleOrchestrate = async (projectId, unitId) => {
    const standardEl = document.getElementById(`standard-select-${projectId}`);
    const standardId = standardEl?.value;
    if (!standardId) {
      dispatch(addNotification({ message: 'Please select a Content Standard before generating.', variant: 'warning' }));
      return;
    }

    setOrchestratingUnits(prev => ({ ...prev, [unitId]: true }));
    try {
      await api.post(`/tenant/${slug}/projects/${projectId}/orchestrate-unit`, {
        unit_id: unitId,
        content_standard_id: parseInt(standardId),
      });
      dispatch(addNotification({ message: 'Unit drafting started in the background.', variant: 'info' }));
      fetchProjects();
    } catch (err) {
      const status = err?.response?.status;
      if (status >= 400 && status < 500) {
        dispatch(addNotification({
          message: err.response?.data?.detail || 'Unit drafting could not be started. Check content standard and credits.',
          variant: 'danger',
        }));
      } else {
        // Background task likely started despite the network error
        dispatch(addNotification({
          message: 'Drafting is running in the background. The unit status will update shortly.',
          variant: 'info',
        }));
        setTimeout(fetchProjects, 5000);
      }
    } finally {
      setOrchestratingUnits(prev => ({ ...prev, [unitId]: false }));
    }
  };

  const handleFinalizeToggle = async (projectId, unitId, blockId, currentStatus) => {
    const key = `${projectId}-${unitId}`;
    const isFinalized = currentStatus === 'finalized';
    setFinalizingUnit(prev => ({ ...prev, [key]: true }));
    try {
      const res = await api.patch(`/tenant/${slug}/admin/content-blocks/${blockId}/${isFinalized ? 'unfinalize' : 'finalize'}`);
      setContentBlocksByProject(prev => ({
        ...prev,
        [projectId]: { ...prev[projectId], [unitId]: { ...prev[projectId]?.[unitId], status: res.data.status } },
      }));
      dispatch(addNotification({
        message: isFinalized ? 'Unit content reverted to draft.' : 'Unit content finalized — now visible to learners.',
        variant: 'success',
      }));
    } catch (err) {
      dispatch(addNotification({
        message: err.response?.data?.detail || 'Could not update this unit\'s review status.',
        variant: 'danger',
      }));
    } finally {
      setFinalizingUnit(prev => ({ ...prev, [key]: false }));
    }
  };

  const isReadyForAssessmentBank = (project) => {
    const units = (project.curriculum_draft || []).flatMap(m => m.children || []);
    if (units.length === 0) return false;
    const hasBlueprint = (projectBlueprints[project.id] || 0) > 0;
    const unitsDone = units.every(unit => {
      const status = project.orchestration_progress?.[unit.unit_id];
      return status === 'completed' || status === 'partial_success';
    });
    return hasBlueprint && (unitsDone || project.status === 'approved' || project.status === 'completed');
  };

  const renderProjectStepper = (project) => {
    let currentStep = 1;
    let label = "Curriculum Planning";
    if (project.current_stage === 'blueprint' || project.current_stage === 'ckb_processing') {
        currentStep = 2; label = "Knowledge Engineering";
    }
    else if (project.current_stage === 'ckb_completed' || project.current_stage === 'orchestration') {
      currentStep = isReadyForAssessmentBank(project) ? 4 : 3;
      label = currentStep === 4 ? "Ready for Assessment" : "Instructional Drafting";
    }
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between mb-1 small fw-bold text-uppercase text-primary">
          <span>{label}</span>
          <span>Step {currentStep} of 4</span>
        </div>
        <ProgressBar now={(currentStep / 4) * 100} style={{ height: '6px' }} />
      </div>
    );
  };

  // Loading skeleton — more accessible and professional than a bare spinner
  const ProjectSkeleton = () => (
    <Card className="mb-4" aria-hidden="true">
      <Card.Body>
        <div className="skeleton mb-3" style={{ height: 20, width: '55%' }} />
        <div className="skeleton mb-2" style={{ height: 6 }} />
        <div className="skeleton mb-4" style={{ height: 12, width: '35%' }} />
        <div className="skeleton" style={{ height: 80 }} />
      </Card.Body>
    </Card>
  );

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">
      <Joyride steps={steps} run={runTour} continuous={true} callback={handleJoyrideCallback} />
      <NotificationBanner />

      <Container fluid className="px-4 flex-grow-1">
        <Row className="mb-4 mt-4 pipeline-header">
          <Col className="d-flex justify-content-between align-items-center">
            {/* h1 for page landmark — screen readers find it first */}
            <div>
              <h1 className="h3 fw-bold mb-0">Production Pipeline</h1>
              <p className="text-muted small mb-0">
                {loading ? 'Loading projects…' : `${projects.length} project${projects.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              className="btn-pill"
              onClick={() => setRunTour(true)}
              aria-label="Open help guide"
            >
              <span aria-hidden="true">💡</span> Help Guide
            </Button>
          </Col>
        </Row>

        {/* Screen-reader-only status announcement */}
        {loading && <p className="sr-only" aria-live="polite">Loading projects, please wait.</p>}

        {loading ? (
          /* Skeleton cards — friendlier than a bare spinner */
          <div role="status" aria-label="Loading projects">
            {[1, 2, 3].map(i => <ProjectSkeleton key={i} />)}
          </div>
        ) : projects.length === 0 ? (
          /* Empty state */
          <div className="text-center py-12" role="status">
            <p style={{ fontSize: 48, opacity: 0.3 }} aria-hidden="true">📋</p>
            <h2 className="h5 text-muted mb-2">No projects yet</h2>
            <p className="text-muted small">Your projects will appear here once created by the Tenant Admin.</p>
          </div>
        ) : (
          projects.map((project) => {
            const allTopics = (project.curriculum_draft || []).flatMap(mod =>
                (mod.children || []).flatMap(unit => unit.topics || [])
                );
            const showInitialSetup = !project.curriculum_draft || project.curriculum_workflow_status === 'pending_upload' || project.curriculum_workflow_status === 'drafting';
            const uploadedCount = Object.keys(project.orchestration_progress || {}).length;
            const isManualIncomplete = project.curriculum_mode === 'full_manual' && uploadedCount < allTopics.length;

            return (
            <Card key={project.id} className={`mb-4 border-0 shadow-sm project-card-${project.id}`}>
              <div className="bg-primary" style={{height: '4px'}}></div>
              <Card.Body className="p-4 bg-white">
                {renderProjectStepper(project)}

                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div>
                    <h4 className="fw-bold mb-1">{project.name}</h4>
                    <Badge bg="info" text="dark" className="text-uppercase">{project.current_stage.replace("_", " ")}</Badge>
                  </div>
                  <Stack direction="horizontal" gap={2}>
                    <Button variant="outline-primary" size="sm" onClick={() => navigate(`/tenant/${slug}/projects/${project.id}/academic-profile`)}>🎓 View Curriculum</Button>
                    <Button variant="outline-dark" size="sm" onClick={() => navigate(`/graph/${project.id}`)}>📊 View Concept Graph</Button>
                  </Stack>
                </div>

                {showInitialSetup && (
                  <CurriculumInitialStep project={project} tenantSlug={slug} onSuccess={fetchProjects} />
                )}

                {!showInitialSetup && project.current_stage === 'curriculum_mapping' &&
                    project.curriculum_workflow_status !== 'awaiting_self_content' && (
                   <CurriculumMappingEditor
                    projectId={project.id}
                    curriculumDraft={project.curriculum_draft}
                    courseSummary={project.course_summary}
                    onApprove={fetchProjects}
                   />
                )}

                {(project.current_stage === 'content_selection' ||
                project.curriculum_workflow_status === 'awaiting_self_content' ||
                (project.curriculum_mode === 'full_manual' && project.current_stage === 'blueprint')) && (
                 <SelfContentPicker project={project} slug={slug} onRefresh={fetchProjects} />
                )}

                {project.current_stage === 'blueprint' && (
                  <div className="p-5 border rounded text-center bg-white">
                    <h5 className="fw-bold">Knowledge Base Extraction</h5>
                    <Button size="lg"
                    variant={isManualIncomplete ? "secondary" : "primary"}
                    onClick={() => setCkbConfirmModal({ show: true, projectId: project.id })}
                    disabled={isManualIncomplete || project.ckb_status === 'processing'}
                    >
                            "🚀 Start CKB Extraction"
                   
                    </Button>
                        {isManualIncomplete && (
                        <div className="text-danger small mt-2 fw-bold">
                            Constraint: {allTopics.length - uploadedCount} more topics require faculty content.
                        </div>
                    )}

                  </div>
                )}

                {(project.current_stage === 'ckb_processing' || project.ckb_status === 'processing') && (
                  <div className="text-center p-5 bg-white border rounded shadow-sm">
                    <Spinner animation="border" variant="primary" className="mb-3" aria-hidden="true" />
                    <h5 className="fw-bold text-dark">Building Knowledge Graph...</h5>
                    <p className="text-muted small">This background task usually takes 5-10 minutes depending upon the reference book(s) .</p>
                  </div>
                )}

                {(project.current_stage === 'ckb_completed' || project.current_stage === 'orchestration') && (
                  <div className="p-4 border rounded bg-white shadow-sm">
                    <Stack direction="horizontal" gap={2} className="mb-4 border-bottom pb-3">
                        <h5 className="fw-bold mb-0">Production Hub</h5>
                        <div className="ms-auto d-flex gap-2">
                            {/* Step 3 actions — gated by content_generation module */}
                            {canContentGen ? (
                              <>
                                <Button variant="outline-success" size="sm" onClick={() => navigate(`/tenant/${slug}/projects/${project.id}/evidence`)}>🔬 Evidence</Button>
                                <Button variant="outline-info" size="sm" disabled={omRunning[project.id]}
                                  onClick={() => handleRunOutcomeMapping(project.id)}
                                  title="Map concepts to Learning Outcomes using DeepSeek AI">
                                  {omRunning[project.id] ? <><Spinner size="sm" animation="border" aria-hidden="true" /> Mapping…</> : '🗺 Outcome Map'}
                                </Button>
                              </>
                            ) : null}
                            {/* Step 4 actions — gated by assessment_bank module */}
                            {canAssessBank ? (
                              <>
                                <Button variant="primary" size="sm" onClick={() => navigate(`/tenant/${slug}/projects/${project.id}/blueprint`)}>📝 Create Q.Bank Blueprint</Button>
                                <OverlayTrigger placement="top"
                                  overlay={<Tooltip>{(projectBlueprints[project.id] || 0) === 0 ? "Setup assessment blueprints first" : "Generate Full Question Bank"}</Tooltip>}>
                                  <span>
                                    <Button size="sm" variant="success" className="fw-bold"
                                      disabled={!isReadyForAssessmentBank(project) || generatingBank[project.id] || project.status === 'processing'}
                                      onClick={() => handleTriggerFullAssessment(project.id)}>
                                      {generatingBank[project.id] || project.status === 'processing' ? (
                                        <><Spinner size="sm" animation="border" className="me-1" aria-hidden="true" />Generating...</>
                                      ) : '🚀 Generate Q.Bank'}
                                    </Button>
                                  </span>
                                </OverlayTrigger>
                              </>
                            ) : null}
                        </div>
                    </Stack>

                    {/* Locked callout when content_generation not subscribed */}
                    {!canContentGen && (
                      <LockedFeature
                        title="AI Content Generation — Not Subscribed"
                        reason="Steps 3 & 4 (AI instructional drafting and Q.Bank generation) are not included in your current subscription. Contact your administrator to upgrade."
                      />
                    )}
                    {/* Locked callout when only assessment_bank not subscribed (content_gen is) */}
                    {canContentGen && !canAssessBank && (
                      <LockedFeature
                        title="Assessment Bank Generation — Not Subscribed"
                        reason="Automated Q.Bank generation (Step 4) is not included in your current subscription. Contact your administrator to upgrade."
                      />
                    )}

                            {(project.status === 'completed' || project.status === 'approved') && (
                              <div className="mb-4 p-3 border rounded bg-light shadow-sm d-flex justify-content-between align-items-center animate__animated animate__fadeIn border-success">
                                <div className="d-flex align-items-center">
                                  <Badge bg="success" className="me-2 rounded-circle p-2">
                                    <i className="bi bi-check-lg"></i>
                                  </Badge>
                                  <span className="fw-bold text-success">
                                    {project.status === 'approved' ? 'QB Approved' : 'QB Ready'}
                                  </span>
                                </div>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  className="fw-bold shadow-sm"
                                  onClick={() => navigate(`/tenant/${slug}/projects/${project.id}/assessment-history`)}
                                >
                                  👁️ View  Q. Bank & History
                                </Button>
                              </div>
                            )}



                    {/* Content Standard selector with Inspect button (SME: read-only inspect, no clone) */}
                    <Form.Group className="mb-4 p-3 bg-light rounded border">
                      <Form.Label className="small fw-bold text-primary text-uppercase" htmlFor={`standard-select-${project.id}`}>
                        Active Production Standard
                      </Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Select
                          size="sm"
                          id={`standard-select-${project.id}`}
                          className="border-primary"
                          style={{ maxWidth: 360 }}
                          defaultValue={project.content_standard_id}
                          onChange={(e) => handleStandardChange(project.id, e.target.value)}
                        >
                          <option value="">Select Content Standard…</option>
                          {contentStandards.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.name.replaceAll("_", " ")}
                              {s.tenant_id ? ' ★' : ''}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={
                            !document.getElementById(`standard-select-${project.id}`)?.value &&
                            !project.content_standard_id
                          }
                          onClick={() => {
                            const sel = document.getElementById(`standard-select-${project.id}`);
                            const id = sel?.value || project.content_standard_id;
                            if (id) { setInspectStdId(Number(id)); setInspectStdOpen(true); }
                          }}
                          aria-label="Inspect content standard details"
                          title="View full details of the selected standard"
                        >
                          🔍 Inspect
                        </Button>
                      </div>
                      <Form.Text className="text-muted small">
                        The selected standard defines word counts, sections, and pedagogical models for AI generation.
                        {' '}Standards marked <strong>★</strong> are tenant-specific customisations.
                      </Form.Text>
                    </Form.Group>

                    <Table responsive hover className="align-middle">
                      <thead className="table-light small text-uppercase">
                        <tr><th>Unit Title</th><th>Status</th><th scope="col" className="text-end">Action</th></tr>
                      </thead>
                      <tbody>
                        {(project.curriculum_draft || []).flatMap((module, mIdx) =>
                          (module.children || []).flatMap((unit, uIdx) => {

                            // 1. GATEKEEPER: Determine if we show Topics or Units
                            // We ONLY show topic-level rows if the mode is exactly 'full_manual'
                            const showTopics = project.curriculum_mode === 'full_manual';

                            if (showTopics) {
                              // --- FULL MANUAL MODE: RENDER TOPICS ---
                              return (unit.topics || []).map((topic, tIdx) => {
                                const topicId = `${unit.unit_id}_t${tIdx}`;
                                const reactKey = `proj-${project.id}-topic-${topicId}`;
                                const status = project.orchestration_progress?.[topicId] || 'pending';
                                const isEligibleForEditor = status === 'completed' || status === 'partial_success';
                                const isOrchestrating = orchestratingUnits[topicId] || status === 'in_progress' || status === 'generating';

                                return (
                                  <tr key={reactKey}>
                                    <td className="fw-medium small ps-4">
                                      <i className="bi bi-arrow-return-right me-2 text-muted"></i>
                                      {typeof topic === 'string' ? topic : (topic.title || topic.topic_title)}
                                    </td>
                                    <td>
                                      <Badge bg={status === 'completed' ? 'success' : isEligibleForEditor ? 'warning' : 'light'} text="dark" className="px-2 py-1">
                                        {status.replace("_", " ")}
                                      </Badge>
                                    </td>
                                    <td className="text-end">
                                      <Button
                                        size="sm"
                                        variant={isEligibleForEditor ? "outline-primary" : "primary"}
                                        onClick={() => isEligibleForEditor ? navigate(`/editor/${project.id}/${topicId}`) : handleOrchestrate(project.id, topicId)}
                                        disabled={isOrchestrating}
                                      >
                                        {isOrchestrating ? <Spinner size="sm" animation="border" aria-hidden="true" /> : isEligibleForEditor ? '✏️ Edit' : 'Initiate Topic Drafting'}
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              });
                            } else {
                              // --- HYBRID / AI GROUNDED / MANUAL MODE: RENDER UNITS ONLY ---
                              const currentUnitId = unit.unit_id || unit.id || `u-${mIdx}-${uIdx}`;
                              const reactKey = `proj-${project.id}-unit-${currentUnitId}`;
                              const status = project.orchestration_progress?.[currentUnitId] || 'pending';
                              const isEligibleForEditor = status === 'completed' || status === 'partial_success';
                              const isOrchestrating = orchestratingUnits[currentUnitId] || status === 'in_progress' || status === 'generating';

                              return (
                                <tr key={reactKey}>
                                  <td className="fw-medium">{unit.title}</td>
                                  <td>
                                    <Badge bg={status === 'completed' ? 'success' : isEligibleForEditor ? 'warning' : 'light'} text="dark" className="px-2 py-1">
                                      {status.replace("_", " ")}
                                    </Badge>
                                  </td>
                                  <td className="text-end">
                                    {isEligibleForEditor ? (
                                      <div className="d-flex gap-2 justify-content-end align-items-center flex-wrap">
                                        <Button size="sm" variant="outline-primary" onClick={() => navigate(`/editor/${project.id}/${currentUnitId}`)}>
                                          ✏️ View / Edit Content
                                        </Button>
                                        {canReviewContent && contentBlocksByProject[project.id]?.[currentUnitId] && (() => {
                                          const block = contentBlocksByProject[project.id][currentUnitId];
                                          const isFinalized = block.status === 'finalized';
                                          const finalizeKey = `${project.id}-${currentUnitId}`;
                                          return (
                                            <>
                                              <Badge bg={isFinalized ? 'success' : block.status === 'sme_review' ? 'info' : 'secondary'}>
                                                {isFinalized ? 'Finalized' : block.status === 'sme_review' ? 'In Review' : 'Draft'}
                                              </Badge>
                                              <Button
                                                size="sm"
                                                variant={isFinalized ? 'outline-secondary' : 'outline-success'}
                                                disabled={!!finalizingUnit[finalizeKey]}
                                                onClick={() => handleFinalizeToggle(project.id, currentUnitId, block.id, block.status)}
                                              >
                                                {finalizingUnit[finalizeKey]
                                                  ? <Spinner size="sm" animation="border" aria-hidden="true" />
                                                  : isFinalized ? '↩️ Unfinalize' : '✅ Finalize for Learners'}
                                              </Button>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => handleOrchestrate(project.id, currentUnitId)}
                                        disabled={isOrchestrating}
                                      >
                                        {isOrchestrating ? (
                                          <><Spinner size="sm" animation="border" className="me-1" aria-hidden="true" /> Generating...</>
                                        ) : (
                                          'Initiate Unit Drafting'
                                        )}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              );
                            }
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          )})
        )}
      </Container>

      {/* Content Standard Inspect Modal — SME read-only, no clone button */}
      <ContentStandardInspectModal
        show={inspectStdOpen}
        onHide={() => setInspectStdOpen(false)}
        standardId={inspectStdId}
        slug={slug}
        onClone={undefined}
      />

      {/* Blueprint Selection Modal */}
      <Modal show={showBlueprintModal} onHide={() => setShowBlueprintModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold">Select Assessment Blueprint</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">Please choose a blueprint to guide the generation of your Question Bank.</p>
          <Table hover responsive className="align-middle">
            <thead className="table-light small text-uppercase">
              <tr>
                <th>Blueprint Name</th>
                <th scope="col" className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {availableBlueprints.map(bp => (
                <tr key={bp.id}>
                  <td>
                  <div className="fw-bold text-primary">{bp.name || `Blueprint #${bp.id}`}</div>
                  <div className="small text-muted">
                  Type: <Badge bg="light" text="dark" className="border">{bp.mode?.replace('_', ' ')}</Badge>
                  <span className="ms-2">Created: {new Date(bp.created_at).toLocaleDateString()}</span>
                </div>
                </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="success"
                      className="fw-bold"
                      onClick={() => executeAssessmentGeneration(bp.id)}
                    >
                      Use this Blueprint
                    </Button>
                  </td>
                </tr>
              ))}
              {availableBlueprints.length === 0 && (
                <tr>
                  <td colSpan="2" className="text-center py-4 text-muted">
                    No blueprints found. Please create a blueprint first.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/* --- CKB Confirmation Modal --- */}
      <Modal 
        show={ckbConfirmModal.show} 
        onHide={() => setCkbConfirmModal({ show: false, projectId: null })}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton className="bg-warning text-dark border-0">
          <Modal.Title className="fw-bold">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Heavy Processing Required
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center">
          <h5 className="fw-bold mb-3">Initiate Knowledge Graph Extraction?</h5>
          <p className="text-muted mb-4">
            You are about to command the AI to read, map, and extract concepts from this textbook. 
          </p>
          <div className="bg-light p-3 rounded border text-start small">
            <ul className="mb-0">
              <li className="mb-2"><strong>Time Expectation:</strong> For large textbooks (1,000+ pages), this process can take <strong>up to 60 minutes</strong>.</li>
              <li className="mb-2"><strong>Background Task:</strong> This runs entirely on our servers. You may close this tab, shut down your computer, or work on other projects.</li>
              <li><strong>Notification:</strong> You will see the status update on your dashboard once complete.</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center pb-4">
          <Button variant="light" className="px-4 border" onClick={() => setCkbConfirmModal({ show: false, projectId: null })}>
            Cancel
          </Button>
          <Button variant="primary" className="px-5 fw-bold" onClick={handleInitiateCKB}>
            Confirm & Start Extraction
          </Button>
        </Modal.Footer>
      </Modal>

      <AppFooter />
    </div>
  );
};

export default DashboardPage;
